import { auth } from '../firebase';
import { getBackendBaseUrl, throwBackendHttpError } from './api';

const NOTIFICATION_STREAM_PATH = 'api/notifications/stream';

// The server renews each stream after four minutes. One that stayed open this
// long was healthy, so the next failure restarts the backoff.
export const HEALTHY_STREAM_MS = 30 * 1000;
export const RECONNECT_BASE_MS = 1000;
export const RECONNECT_MAX_MS = 60 * 1000;
// A token refused again right after a forced refresh is not fixed by retrying
// quickly (for example, access was withdrawn).
export const AUTH_RETRY_MS = 5 * 60 * 1000;

const withJitter = (ms) => Math.round(ms / 2 + (Math.random() * ms) / 2);

export const reconnectDelayMs = (failures) => withJitter(
    Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** Math.max(0, failures - 1)),
);

const isEventStream = (response) => /^text\/event-stream\b/i.test(
    response.headers?.get?.('Content-Type') || '',
);

/**
 * Incrementally parse a text/event-stream body as the HTML server-sent events
 * spec does. Calls onEvent({ type, data, lastEventId }) per dispatched event;
 * an event cut off before its closing blank line is never dispatched.
 * Comments (heartbeats), `retry` and unknown fields are ignored: the caller's
 * own backoff decides when to reconnect.
 */
export function createSseParser(onEvent) {
    let buffer = '';
    let data = '';
    let type = '';
    let lastEventId = '';

    const processLine = (line) => {
        if (line === '') {
            const event = data === ''
                ? null
                : { type: type || 'message', data: data.slice(0, -1), lastEventId };
            data = '';
            type = '';
            if (event) onEvent(event);
            return;
        }
        if (line.startsWith(':')) return;
        const colon = line.indexOf(':');
        const field = colon === -1 ? line : line.slice(0, colon);
        let value = colon === -1 ? '' : line.slice(colon + 1);
        if (value.startsWith(' ')) value = value.slice(1);
        if (field === 'event') {
            type = value;
        } else if (field === 'data') {
            data += `${value}\n`;
        } else if (field === 'id' && !value.includes('\0')) {
            lastEventId = value;
        }
    };

    return {
        push(text) {
            buffer += text;
            let start = 0;
            for (let index = 0; index < buffer.length; index += 1) {
                const char = buffer[index];
                if (char !== '\n' && char !== '\r') continue;
                // A CR ending this chunk may be the first half of a CRLF.
                if (char === '\r' && index + 1 === buffer.length) break;
                processLine(buffer.slice(start, index));
                if (char === '\r' && buffer[index + 1] === '\n') index += 1;
                start = index + 1;
            }
            buffer = buffer.slice(start);
        },
    };
}

/**
 * Keep the signed-in user's notification stream open until the returned
 * close() is called. Each connection sends the Firebase ID token in the
 * Authorization header, like callBackend, and resumes after the highest
 * notification id delivered so far.
 */
export function openNotificationStream({ onEvent }) {
    const controller = new AbortController();
    const { signal } = controller;
    let retryTimer = null;
    // The server may resend a refreshed older notification after newer ones,
    // so resume after the highest id rather than the most recent one.
    let resumeAfterId = 0;
    let failures = 0;
    let authRefusals = 0;

    const schedule = (delayMs) => {
        retryTimer = setTimeout(() => {
            retryTimer = null;
            void connect();
        }, delayMs);
    };

    const readEvents = async (body) => {
        const reader = body.getReader();
        const decoder = new TextDecoder();
        let renew = false;
        const parser = createSseParser((event) => {
            if (event.type === 'reconnect') {
                renew = true;
                return;
            }
            const eventId = Number(event.lastEventId);
            if (Number.isSafeInteger(eventId) && eventId > resumeAfterId) {
                resumeAfterId = eventId;
            }
            if (!signal.aborted) onEvent(event);
        });
        while (!renew) {
            const { done, value } = await reader.read();
            if (done) return;
            parser.push(decoder.decode(value, { stream: true }));
        }
        await reader.cancel().catch(() => {});
    };

    async function connect() {
        let openedAt = null;
        let refused = false;
        try {
            // Force a fresh token only after the server refused the last one.
            const token = await auth.currentUser?.getIdToken(authRefusals > 0);
            if (signal.aborted) return;
            if (!token) {
                refused = true;
            } else {
                const headers = { Accept: 'text/event-stream', Authorization: `Bearer ${token}` };
                if (resumeAfterId > 0) headers['Last-Event-ID'] = String(resumeAfterId);
                const response = await fetch(`${getBackendBaseUrl()}/${NOTIFICATION_STREAM_PATH}`, {
                    headers,
                    cache: 'no-store',
                    credentials: 'omit',
                    signal,
                });
                if (response.status === 401 || response.status === 403) {
                    refused = true;
                    // Reports a customer-access denial the way every API call does.
                    await throwBackendHttpError(response).catch(() => {});
                } else if (response.ok && isEventStream(response)) {
                    authRefusals = 0;
                    openedAt = Date.now();
                    await readEvents(response.body);
                }
            }
        } catch {
            // A network or stream error is retried below like any other failure.
        }
        if (signal.aborted) return;
        if (refused) {
            authRefusals += 1;
            if (authRefusals === 1) {
                schedule(0);
            } else {
                const delayMs = withJitter(AUTH_RETRY_MS);
                console.warn(`Notification stream sign-in was refused; retrying in ${Math.round(delayMs / 60000)} min.`);
                schedule(delayMs);
            }
            return;
        }
        const healthy = openedAt !== null && Date.now() - openedAt >= HEALTHY_STREAM_MS;
        failures = healthy ? 1 : failures + 1;
        const delayMs = reconnectDelayMs(failures);
        if (!healthy) {
            console.warn(`Notification stream unavailable; retrying in ${Math.round(delayMs / 1000)} s.`);
        }
        schedule(delayMs);
    }

    void connect();

    return () => {
        controller.abort();
        if (retryTimer !== null) {
            clearTimeout(retryTimer);
            retryTimer = null;
        }
    };
}
